import { useState, useEffect, Fragment } from 'react';
import apiService from '../services/api';
import AddOfferCouponModal from './AddOfferCouponModal';
import { useTranslation } from '../context/TranslationContext';
import { localizedField } from '../utils/localizedField';
import { formatPrice } from '../utils/formatPrice';
import { Button, Badge } from '@mantine/core';
import { IconPlus, IconCopy } from '@tabler/icons-react';
import './AdminProducts.css';

function formatDateTime(str) {
  if (!str) return '';
  const d = new Date(str);
  return d.toLocaleDateString('en-CH', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function parseConfig(rc) {
  if (!rc) return {};
  try { return typeof rc === 'string' ? JSON.parse(rc) : rc; } catch { return {}; }
}

const RULE_LABELS = {
  percentage: 'Percentage',
  fixed: 'Fixed',
  free_nth_order: 'Free Nth Order',
  min_order_discount: '% Above Min.',
  free_shipping: 'Free Shipping',
  buy_x_pay_y: 'Buy X Pay Y',
};

function getOrdinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function getOfferValueDisplay(offer) {
  const config = parseConfig(offer.rule_config);
  switch (offer.rule_type) {
    case 'percentage':
      return `${offer.value}%`;
    case 'fixed':
      return formatPrice(offer.value);
    case 'free_nth_order': {
      const n = config.nth_order || '?';
      const ordinal = typeof n === 'number' ? getOrdinal(n) : `${n}th`;
      const reward = offer.reward_name || offer.variant_id || '';
      return `Every ${ordinal}${reward ? ` — ${reward}` : ''}`;
    }
    case 'min_order_discount':
      return `${offer.value}% (min ${formatPrice(config.min_amount || 0)})`;
    case 'free_shipping':
      return '—';
    case 'buy_x_pay_y': {
      const parts = [`Buy ${config.buy_qty || '?'} / Pay ${config.pay_qty || '?'}`];
      if (offer.reward_name) parts.push(offer.reward_name);
      return parts.join(' — ');
    }
    default:
      return offer.value || '—';
  }
}

function getOfferScope(offer) {
  if (offer.product_sku) return `SKU: ${offer.product_sku}`;
  if (offer.category) return `Cat: ${offer.category}`;
  return 'All Products';
}

export default function AdminCoupons() {
  const { langCode } = useTranslation();
  const [offers, setOffers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showOfferCouponModal, setShowOfferCouponModal] = useState(false);
  const [cloneTarget, setCloneTarget] = useState(null);

  const [expandedCouponId, setExpandedCouponId] = useState(null);
  const [usageData, setUsageData] = useState({});
  const [usageLoading, setUsageLoading] = useState({});

  const fetchData = async () => {
    try {
      setLoading(true);
      const [offersData, couponsData] = await Promise.all([
        apiService.getOffers(),
        apiService.getCoupons()
      ]);
      setOffers(offersData);
      setCoupons(couponsData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleOfferActive = async (id, currentActive) => {
    try {
      await apiService.updateOffer(id, { active: !currentActive });
      fetchData();
    } catch(err) {
      console.error(err);
    }
  };

  const toggleCouponActive = async (id, currentActive) => {
    try {
      await apiService.updateCoupon(id, { active: !currentActive });
      fetchData();
    } catch(err) {
      console.error(err);
    }
  };

  const toggleUsage = async (couponId) => {
    if (expandedCouponId === couponId) {
      setExpandedCouponId(null);
      return;
    }
    setExpandedCouponId(couponId);
    if (!usageData[couponId]) {
      setUsageLoading(prev => ({ ...prev, [couponId]: true }));
      try {
        const orders = await apiService.getCouponUsage(couponId);
        setUsageData(prev => ({ ...prev, [couponId]: orders }));
      } catch (err) {
        console.error(err);
        setUsageData(prev => ({ ...prev, [couponId]: [] }));
      } finally {
        setUsageLoading(prev => ({ ...prev, [couponId]: false }));
      }
    }
  };

  const getOfferStatus = (o) => {
    const now = new Date();
    const expired = new Date(o.end_time) < now;
    if (expired) return 'expired';
    if (!o.active) return 'disabled';
    if (new Date(o.start_time) > now) return 'scheduled';
    return 'active';
  };

  const isExpired = (end_time) => new Date(end_time) < new Date();

  const statusColor = { active: 'green', scheduled: 'blue', expired: 'gray', disabled: 'gray' };

  return (
    <div className="admin-products dashboard-view-wrapper">
      <header className="dashboard-page-header">
        <div>
          <h1 className="dashboard-page-title">Coupons & Offers</h1>
          <p className="dashboard-page-description">Create and manage pricing rules and coupon codes.</p>
        </div>
        <div className="header-actions">
          <Button color="indigo" leftSection={<IconPlus size={16} />} onClick={() => setShowOfferCouponModal(true)}>
            Add Offer / Coupon
          </Button>
        </div>
      </header>

      {loading ? <p role="status" aria-live="polite">Loading discounts…</p> : (
        <>
          <h3 style={{ margin: '1.5rem 0 1rem' }}>Offers</h3>
          <div className="table-responsive" style={{ marginBottom: '2rem' }}>
            <table className="admin-table">
              <caption className="sr-only">List of pricing offer rules</caption>
              <thead>
                <tr>
                  <th scope="col">Description</th>
                  <th scope="col">Rule Type</th>
                  <th scope="col">Value / Config</th>
                  <th scope="col">Scope</th>
                  <th scope="col">Period</th>
                  <th scope="col">Status</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {offers.map(o => {
                  const status = getOfferStatus(o);
                  return (
                    <tr key={o.id}>
                      <td>
                        {localizedField(o, 'description', langCode) || 'No description'}
                        {o.business_only ? (
                          <Badge size="sm" variant="light" color="indigo" style={{ marginLeft: 6 }}>
                            Business
                          </Badge>
                        ) : null}
                      </td>
                      <td>
                        <Badge size="sm" variant="light" color={statusColor[status] || 'gray'}>
                          {RULE_LABELS[o.rule_type] || o.rule_type}
                        </Badge>
                      </td>
                      <td style={{ fontSize: '0.875rem' }}>{getOfferValueDisplay(o)}</td>
                      <td style={{ fontSize: '0.875rem' }}>{getOfferScope(o)}</td>
                      <td style={{ fontSize: '0.8125rem' }}>
                        {formatDateTime(o.start_time)}<br />{formatDateTime(o.end_time)}
                      </td>
                      <td>
                        <Badge size="sm" variant="filled" color={statusColor[status]}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </Badge>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          {status !== 'expired' && (
                            <Button
                              size="xs"
                              variant={o.active ? 'light' : 'filled'}
                              color={o.active ? 'gray' : 'blue'}
                              onClick={() => toggleOfferActive(o.id, o.active)}
                              style={{ minWidth: 80 }}
                              aria-label={`${o.active ? 'Disable' : 'Enable'} offer: ${localizedField(o, 'description', langCode) || RULE_LABELS[o.rule_type] || o.rule_type}`}
                            >
                              {o.active ? 'Disable' : 'Enable'}
                            </Button>
                          )}
                          <Button
                            size="xs"
                            variant="default"
                            leftSection={<IconCopy size={14} />}
                            onClick={() => setCloneTarget({ type: 'offer', data: o })}
                            aria-label={`Clone offer: ${localizedField(o, 'description', langCode) || RULE_LABELS[o.rule_type] || o.rule_type}`}
                          >
                            Clone
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {offers.length === 0 && (
                  <tr><td colSpan="7">No offers found.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <h3 style={{ margin: '1.5rem 0 1rem' }}>Coupons</h3>
          <div className="table-responsive">
            <table className="admin-table">
              <caption className="sr-only">List of coupon codes with usage</caption>
              <thead>
                <tr>
                  <th scope="col">Code</th>
                  <th scope="col">Description</th>
                  <th scope="col">Type</th>
                  <th scope="col">Value</th>
                  <th scope="col">Usage</th>
                  <th scope="col">Status</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map(c => {
                  const expired = isExpired(c.end_time);
                  const isExpanded = expandedCouponId === c.id;
                  const orders = usageData[c.id];
                  return (
                    <Fragment key={c.id}>
                      <tr>
                        <td>
                          <strong>{c.code}</strong>
                          {c.business_only ? (
                            <Badge size="sm" variant="light" color="indigo" style={{ marginLeft: 6 }}>
                              Business
                            </Badge>
                          ) : null}
                        </td>
                        <td>{c.description || 'No description'}</td>
                        <td>{c.rule_type === 'percentage' ? 'Percentage' : 'Fixed'}</td>
                        <td>{c.rule_type === 'percentage' ? `${c.value}%` : formatPrice(c.value)}</td>
                        <td>
                          <span style={{ fontSize: '0.875rem', color: '#4a5568' }}>
                            {c.used_count ?? 0}{c.max_uses > 0 ? ` / ${c.max_uses}` : ''}
                          </span>
                        </td>
                        <td>
                          {expired ? <Badge size="sm" variant="filled" color="gray">Expired</Badge> :
                           c.active ? <Badge size="sm" variant="filled" color="green">Active</Badge> :
                           <Badge size="sm" variant="filled" color="gray">Inactive</Badge>}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <Button
                              size="xs"
                              variant="default"
                              onClick={() => toggleUsage(c.id)}
                              style={{ minWidth: 80 }}
                              aria-expanded={isExpanded}
                              aria-controls={`coupon-usage-${c.id}`}
                              aria-label={`${isExpanded ? 'Collapse' : 'Show'} usage for coupon ${c.code}`}
                            >
                              {isExpanded ? 'Collapse' : 'Usage'}
                            </Button>
                            {!expired && (
                              <Button
                                size="xs"
                                variant={c.active ? 'light' : 'filled'}
                                color={c.active ? 'gray' : 'blue'}
                                onClick={() => toggleCouponActive(c.id, c.active)}
                                style={{ minWidth: 80 }}
                                aria-label={`${c.active ? 'Disable' : 'Enable'} coupon ${c.code}`}
                              >
                                {c.active ? 'Disable' : 'Enable'}
                              </Button>
                            )}
                            <Button
                              size="xs"
                              variant="default"
                              leftSection={<IconCopy size={14} />}
                              onClick={() => setCloneTarget({ type: 'coupon', data: c })}
                              aria-label={`Clone coupon ${c.code}`}
                            >
                              Clone
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="nested-row" id={`coupon-usage-${c.id}`}>
                          <td colSpan="7" className="nested-cell">
                            {usageLoading[c.id] ? (
                              <p role="status" aria-live="polite" style={{ padding: '1rem', color: '#4a5568' }}>Loading usage data…</p>
                            ) : orders && orders.length > 0 ? (
                              <div style={{ padding: '0.5rem' }}>
                                <table className="admin-table" style={{ fontSize: '0.875rem' }}>
                                  <caption className="sr-only">Orders that used coupon {c.code}</caption>
                                  <thead>
                                    <tr>
                                      <th scope="col">Order ID</th>
                                      <th scope="col">Customer</th>
                                      <th scope="col">Date</th>
                                      <th scope="col">Total</th>
                                      <th scope="col">Payment</th>
                                      <th scope="col">Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {orders.map(o => (
                                      <tr key={o.id}>
                                        <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{o.id.substring(0, 8)}…</td>
                                        <td>{o.user_name}<br /><span style={{ fontSize: '0.75rem', color: '#718096' }}>{o.user_email}</span></td>
                                        <td>{formatDateTime(o.created_at)}</td>
                                        <td>{formatPrice(o.total_price)}</td>
                                        <td>{o.payment_method}</td>
                                        <td>{o.status}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p style={{ padding: '1rem', color: '#718096' }}>No orders have used this coupon yet.</p>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {coupons.length === 0 && (
                  <tr><td colSpan="7">No coupons found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {(showOfferCouponModal || cloneTarget) && (
        <AddOfferCouponModal
          onClose={() => { setShowOfferCouponModal(false); setCloneTarget(null); }}
          onSaved={async () => {
            setShowOfferCouponModal(false);
            setCloneTarget(null);
            await fetchData();
          }}
          cloneOffer={cloneTarget?.type === 'offer' ? cloneTarget.data : null}
          cloneCoupon={cloneTarget?.type === 'coupon' ? cloneTarget.data : null}
        />
      )}
    </div>
  );
}
